import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { formatDate } from '@app/helpers/date.helper';
import { setCertificate } from '@app/store/actions/certificate.actions';
import { UserCertificate } from '@app/store/models';
import { GlobalState } from '@app/store/store';
import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { LocalStorageService } from 'ngx-webstorage';
import {
  mapCertificateListItemFallback,
  mapUserCertificateToCertificateList,
} from '@app/helpers/certificate.helpers';
import {
  catchError,
  forkJoin,
  lastValueFrom,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { CorrelationIdService } from '../correlation-id.service';
import {
  ActionReportResponse,
  CertificateList,
  CertificateListItem,
  RenewalData,
  ReportLogs,
  Tokens,
} from './../../store/models/certificate.model';

@Injectable({
  providedIn: 'root',
})
export class CertificateService {
  baseUrl: string = '/ncfr-api/certificates';
  public certID: string = '';
  correlationId: string;

  constructor(
    private httpClient: HttpClient,
    private localStorageService: LocalStorageService,
    private store: Store<GlobalState>,
    private correlationIdService: CorrelationIdService
  ) {
    const localSession = this.localStorageService.retrieve('session');
    if (localSession?.userInfo) {
      this.certID = localSession?.userInfo.username;
    } else if (environment.production) {
      // Puliamo la sessione solo in produzione
      this.localStorageService.clear('session');
    }

    this.correlationIdService
      .getCorrelationId()
      .subscribe(value => (this.correlationId = value));
  }

  // lista di certid, uida e otpType
  getCertificateList(): Observable<CertificateListItem[]> {
    return this.httpClient.get<[]>(this.baseUrl, {
      headers: {
        'InfoCert-Correlation-ID': this.correlationId,
      },
    });
  }

  // lista multicertificato
  getMultiCertificateList(signers: []): Observable<any> {
    /*
    return this.httpClient.get<[]>('../../assets/certList.stub.json', {
      params: { signers },
    });
    */
    return this.httpClient.get<CertificateList[]>('/ncfr-api/v1/certificate', {
      headers: {
        'InfoCert-Correlation-ID': this.correlationId,
      },
    });
  }

  /**
   * Lista multicert: usa v1/certificate; se vuota o in errore, fallback su GET /certificates.
   */
  getMultiCertificateListWithFallback(): Observable<CertificateList[]> {
    return this.getMultiCertificateList([]).pipe(
      switchMap(v1List => {
        const list = Array.isArray(v1List) ? v1List : [];
        if (list.length > 0) {
          return of(list);
        }

        console.warn(
          '[CertificateService] /v1/certificate vuota: fallback su /certificates'
        );
        return this.loadCertificateListFromCertificatesEndpoint();
      }),
      catchError(err => {
        console.warn(
          '[CertificateService] /v1/certificate in errore: fallback su /certificates',
          err
        );
        return this.loadCertificateListFromCertificatesEndpoint();
      })
    );
  }

  private loadCertificateListFromCertificatesEndpoint(): Observable<
    CertificateList[]
  > {
    return this.getCertificateList().pipe(
      switchMap(items => {
        const list = items ?? [];
        if (list.length === 0) {
          return of([]);
        }

        return forkJoin(
          list.map(item =>
            this.getCertificateByCertId(item.certId).pipe(
              map(certificate =>
                mapUserCertificateToCertificateList(certificate, item)
              ),
              catchError(() => of(mapCertificateListItemFallback(item)))
            )
          )
        );
      })
    );
  }

  getCertificateByCertId(certId: string): Observable<UserCertificate> {
    return this.httpClient.get<UserCertificate>(`${this.baseUrl}/${certId}`, {
      headers: {
        'InfoCert-Correlation-ID': this.correlationId,
      },
    });
  }

  getCertificate(): Observable<UserCertificate> {
    return this.httpClient
      .get<UserCertificate>(this.baseUrl + '/' + this.certID, {
        headers: {
          'InfoCert-Correlation-ID': this.correlationId,
        },
      })
      .pipe(
        tap(userCertificate => {
          this.store.dispatch(setCertificate(userCertificate));
          //this.localStorageService.store('certificate', userCertificate);
          return userCertificate;
        })
      );
  }

  async getPromiseCertificate(): Promise<UserCertificate> {
    const userCertificate = await lastValueFrom(
      this.httpClient.get<UserCertificate>(this.baseUrl + '/' + this.certID, {
        headers: {
          'InfoCert-Correlation-ID': this.correlationId,
        },
      })
    );
    this.store.dispatch(setCertificate(userCertificate));
    return userCertificate;
  }

  emitCertificate(puk: string, pin: any, otp: string): Observable<any> {
    return this.httpClient.post<any>(
      this.baseUrl + '/' + this.certID + '/emission',
      { puk, pin },
      {
        headers: {
          'X-PATCH': 'true',
          'X-OTP': otp,
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  emitAutomaticCertificate(pin: number, otp: any): Observable<any> {
    return this.httpClient.post<any>(
      this.baseUrl + '/' + this.certID + '/emission',
      { pin },
      {
        headers: {
          'X-PATCH': 'true',
          'X-OTP': otp,
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  emitEsealCertificate(pin: number, otp: string): Observable<any> {
    return this.httpClient.post<any>(
      this.baseUrl + '/' + this.certID + '/emissionEseal',
      { pin },
      {
        headers: {
          'X-PATCH': 'true',
          'X-OTP': otp,
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  emitEsealTrissCertificate(
    pin: string,
    puk: string,
    otp: string
  ): Observable<any> {
    return this.httpClient.post<any>(
      this.baseUrl + '/' + this.certID + '/emissionEseal',
      { pin, puk },
      {
        headers: {
          'X-PATCH': 'true',
          'X-OTP': otp,
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  updatePIN(oldPin: number, newPin: number, otp: string): Observable<any> {
    return this.httpClient.put<any>(
      this.baseUrl + '/' + this.certID + '/pin',
      { oldPin, newPin },
      {
        headers: {
          'X-PATCH': 'true',
          'X-OTP': otp,
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  // Report PkBox
  getDailyReport(): Observable<any> {
    return this.httpClient.get<any>(
      this.baseUrl + '/' + this.certID + '/services/daily-report',
      {
        headers: {
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  updateDailyReport(active: string): Observable<any> {
    return this.httpClient.put<any>(
      this.baseUrl + '/' + this.certID + '/services/daily-report',
      { active },
      {
        headers: {
          'Content-Type': 'application/json',
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  manageCertificate(
    pin: number,
    operation: string,
    otp: string
  ): Observable<any> {
    return this.httpClient.put<any>(
      this.baseUrl + '/' + this.certID + '/manage-cert',
      { pin, operation },
      {
        headers: {
          'X-PATCH': 'true',
          'X-OTP': otp,
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  synchronizeDevice(otp1: number, otp2: number): Observable<any> {
    return this.httpClient.put<any>(
      this.baseUrl + '/' + this.certID + '/manage-hw',
      { otp1, otp2 },
      {
        headers: {
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  getPushDevices(): Observable<any> {
    return this.httpClient.get<any>(
      this.baseUrl + '/' + this.certID + '/devices?type=PUSH',
      {
        headers: {
          ignoreInterceptor: 'true',
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  revokeDevice(bindId: string): Observable<any> {
    return this.httpClient.delete<any>(
      this.baseUrl + '/' + this.certID + '/devices/' + bindId,
      {
        headers: {
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  //cambio telefono per i triss
  changeTelephoneNumber(
    telephone: string,
    otp: string,
    confirmation_code: string
  ): Observable<any> {
    return this.httpClient.put<any>(
      this.baseUrl + '/' + this.certID,
      { telephone },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-OTP': otp,
          'X-OTP-NEWMOBILE': confirmation_code,
          'X-OTP2': otp,
          'X-PATCH': 'true',
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  //cambio telefono per i non triss
  changeTelephoneNumberNotTriss(
    telephone: string,
    otp: string
  ): Observable<any> {
    return this.httpClient.put<any>(
      this.baseUrl + '/' + this.certID,
      { telephone },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-OTP': otp,
          'X-OTP2': otp,
          'X-PATCH': 'true',
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  changeEmail(email: string, otp: string): Observable<any> {
    return this.httpClient.put<any>(
      this.baseUrl + '/' + this.certID,
      { email },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-PATCH': 'true',
          'X-OTP': otp,
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  /* DEPRECATO: adesso il dato eSeal si trova nella session
  async checkEseal(): Promise<any> {
    const isEseal = await lastValueFrom(
      this.httpClient.get<any>(this.baseUrl + '/checkEseal', {
        headers: {
          ignoreLoading: 'true',
        },
      })
    );
    return isEseal;
  } */

  downloadCertificate(downloadFormat: string): Observable<any> {
    var downloadUrl = this.getDownloadUrl(downloadFormat);
    window.open(downloadUrl);

    return this.httpClient.get<any>(downloadUrl, {
      headers: {
        ignoreInterceptor: 'true',
        'InfoCert-Correlation-ID': this.correlationId,
      },
    });
  }

  getDownloadUrl(downloadFormat: string = ''): string {
    const unix = Math.round(+new Date() / 1000);
    const downloadUrl = `${this.baseUrl}/${this.certID}/file?ts=${unix}&format=${downloadFormat}`;

    return downloadUrl;
  }

  // Report ICSS
  async getActionReport(
    startDate: string,
    endDate: string
  ): Promise<ReportLogs[]> {
    if (!this.certID) return Promise.resolve([]);
    console.count('getActionReport');
    console.log('getActionReport', startDate, endDate);
    endDate = formatDate(moment(endDate).add(1, 'days'));
    const reportResponse: ActionReportResponse = await lastValueFrom(
      this.httpClient.get<ActionReportResponse>(
        this.baseUrl + '/' + this.certID + '/actions',
        {
          headers: {
            'Content-Type': 'application/json',
            ignoreInterceptor: 'true',
            'InfoCert-Correlation-ID': this.correlationId,
          },
          params: { action: 'SIGN', startDate, endDate },
        }
      )
    );

    return reportResponse.logs;
  }

  // RINNOVO

  getRenewalState(): Observable<RenewalData> {
    return this.httpClient.get<any>(
      this.baseUrl + '/' + this.certID + '/renewal',
      {
        headers: {
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  contractSign(
    pin: number,
    userConfirmedPersonalData: boolean,
    otp: string,
    statements: string[],
  ): Observable<any> {
    return this.httpClient.post<any>(
      this.baseUrl + '/' + this.certID + '/renewal/contract',
      { pin, userConfirmedPersonalData, statements },
      {
        headers: {
          'X-PATCH': 'true',
          'X-OTP': otp,
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  renewCert(
    newCertificatePin: number,
    otp?: string,
    passphrase?: string
  ): Observable<any> {
    let payload = {};
    if (passphrase !== undefined) {
      payload = { newCertificatePin, passphrase };
    } else {
      payload = { newCertificatePin };
    }
    return this.httpClient.post<any>(
      this.baseUrl + '/' + this.certID + '/renewal/emission',
      payload,
      {
        headers: {
          'X-PATCH': 'true',
          'X-OTP': otp,
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  renewRAO(pswd: string, user: string): Observable<any> {
    return this.httpClient.post<any>(
      this.baseUrl + '/' + this.certID + '/renewal/rao',
      { pswd, user },
      {
        headers: {
          'X-PATCH': 'true',
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  renewCoupon(coupon: string): Observable<any> {
    return this.httpClient.post<any>(
      this.baseUrl + '/' + this.certID + '/renewal/coupon',
      { coupon },
      {
        headers: {
          'X-PATCH': 'true',
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  renewShop(): Observable<any> {
    return this.httpClient.post<any>(
      this.baseUrl + '/' + this.certID + '/renewal/shop',
      {},
      {
        headers: {
          'X-PATCH': 'true',
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  // Token Virtuale
  async getVirtualTokens(): Promise<Tokens[]> {
    const virtualTokenResponse: any = await lastValueFrom(
      this.httpClient.get<any>(this.baseUrl + '/' + this.certID + '/token', {
        headers: {
          'InfoCert-Correlation-ID': this.correlationId,
        },
      })
    );

    return virtualTokenResponse;
  }

  generateVirtualToken(
    description: string,
    expirationDate: string,
    name: string,
    signatures: string | null,
    otp: string
  ): Observable<any> {
    const userId = this.certID;
    let payload = {};
    if (signatures === 'UNLI') {
      payload = {
        description: description,
        expirationDate: expirationDate,
        name: name,
        signatures: null,
        userId: userId,
      };
    } else {
      payload = {
        description: description,
        expirationDate: expirationDate,
        name: name,
        signatures: signatures,
        userId: userId,
      };
    }

    return this.httpClient.post<any>(
      this.baseUrl + '/' + this.certID + '/token',
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'X-OTP': otp,
          'X-PATCH': 'true',
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }
//method for triss token
  trissGenerateVirtualToken(
    description: string,
    name: string,
    otp: string
  ): Observable<any> {
    const userId = this.certID;
    let payload = {};

    payload = {
      description: description,
      name: name,
      userId: userId,
    };

    return this.httpClient.post<any>(
      this.baseUrl + '/' + this.certID + '/token',
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'X-OTP': otp,
          'X-PATCH': 'true',
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  downloadVirtualToken(id: number, otp: string): Observable<any> {
    return this.httpClient.get<any>(
      this.baseUrl + '/' + this.certID + '/token' + '/' + id,
      {
        headers: {
          'X-OTP': otp,
          'X-PATCH': 'true',
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  revokeVirtualToken(id: number, mode: string, otp: string): Observable<any> {
    return this.httpClient.put<any>(
      this.baseUrl + '/' + this.certID + '/token' + '/' + id,
      { mode },
      {
        headers: {
          'X-OTP': otp,
          'X-PATCH': 'true',
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }
}
